import { useState, type ReactNode } from 'react';
import type { FetcherStatus } from '../../types';
import { StatusBadge } from './Badges';

/** One fetcher on the network: its status line always visible, its setup expanding in place,
 *  and — when `onToggle` is given — its own on/off switch. Off takes the fetcher out of every
 *  ladder and every engine and keeps its settings. `alwaysOpen` shows the setup with no
 *  Hide/Set up toggle — for when it is the only card on screen, as in `FetcherTabs`. */
export default function FetcherCard({
  fetcher,
  title,
  children,
  defaultOpen = false,
  alwaysOpen = false,
  enabled,
  onToggle,
}: {
  fetcher?: FetcherStatus;
  title: string;
  children?: ReactNode;
  defaultOpen?: boolean;
  alwaysOpen?: boolean;
  enabled?: boolean;
  onToggle?: (on: boolean) => void;
}) {
  const [opened, setOpen] = useState(defaultOpen);
  const open = alwaysOpen || opened;
  const toggles = !!children && !alwaysOpen;
  const on = enabled !== false;
  return (
    <div
      className={`border border-gray-200 dark:border-gray-700 rounded-lg ${on ? '' : 'opacity-70'}`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => toggles && setOpen((o) => !o)}
          className={`flex-1 min-w-0 flex items-start justify-between gap-3 text-left ${
            toggles ? 'cursor-pointer' : 'cursor-default'
          }`}
          aria-expanded={toggles ? open : undefined}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
              {fetcher && <StatusBadge status={on ? fetcher.status : 'off'} />}
            </div>
            {fetcher?.detail && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {on ? fetcher.detail : 'Switched off: not used by any ladder. Its settings are kept.'}
              </p>
            )}
          </div>
          {toggles && (
            <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 mt-0.5">
              {open ? 'Hide' : 'Set up'}
            </span>
          )}
        </button>
        {onToggle && (
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={`${title} ${on ? 'on' : 'off'}`}
            onClick={() => onToggle(!on)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
              on ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                on ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        )}
      </div>
      {children && open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">{children}</div>
      )}
    </div>
  );
}
